import { useCallback, useEffect } from "react";
import { useMapContext } from "@/hooks/useMap";
import type { FeatureCollection, Geometry } from "geojson";
import type { GeoJSONSource, MapMouseEvent } from "maplibre-gl";

const HIGHLIGHT_SOURCE_ID = "highlight-source";
const HIGHLIGHT_POLYGON_LAYER_ID = "highlight-polygon-layer";
const HIGHLIGHT_LINE_LAYER_ID = "highlight-line-layer";
const HIGHLIGHT_POINT_LAYER_ID = "highlight-point-layer";

const HIGHLIGHT_COLOR = "#FFD700"; // Gold color for highlight
const HIGHLIGHT_STROKE_WIDTH = 4;

export const HighlightLayer: React.FC = () => {
  const { mapInstance, highlightedFeature, clearHighlight } = useMapContext();

  const moveHighlightLayersToTop = useCallback(() => {
    const map = mapInstance.current;
    if (!map) return;

    const highlightLayerOrder = [
      HIGHLIGHT_POLYGON_LAYER_ID,
      `${HIGHLIGHT_POLYGON_LAYER_ID}-outline`,
      HIGHLIGHT_LINE_LAYER_ID,
      HIGHLIGHT_POINT_LAYER_ID,
    ];

    highlightLayerOrder.forEach((layerId) => {
      if (!map.getLayer(layerId)) {
        return;
      }

      try {
        map.moveLayer(layerId);
      } catch (error) {
        console.warn(`Failed to move highlight layer ${layerId}:`, error);
      }
    });
  }, [mapInstance]);

  // Add highlight layers to map
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    // Wait for map to be fully loaded
    const addLayers = () => {
      try {
        // Add source if it doesn't exist
        if (!map.getSource(HIGHLIGHT_SOURCE_ID)) {
          map.addSource(HIGHLIGHT_SOURCE_ID, {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [],
            },
          });
        }

        // Add polygon layer if it doesn't exist
        if (!map.getLayer(HIGHLIGHT_POLYGON_LAYER_ID)) {
          map.addLayer({
            id: HIGHLIGHT_POLYGON_LAYER_ID,
            type: "fill",
            source: HIGHLIGHT_SOURCE_ID,
            filter: [
              "match",
              ["geometry-type"],
              ["Polygon", "MultiPolygon"],
              true,
              false,
            ],
            paint: {
              "fill-color": HIGHLIGHT_COLOR,
              "fill-opacity": 0.3,
              "fill-outline-color": HIGHLIGHT_COLOR,
            },
          });

          // Add polygon outline
          map.addLayer({
            id: `${HIGHLIGHT_POLYGON_LAYER_ID}-outline`,
            type: "line",
            source: HIGHLIGHT_SOURCE_ID,
            filter: [
              "match",
              ["geometry-type"],
              ["Polygon", "MultiPolygon"],
              true,
              false,
            ],
            paint: {
              "line-color": HIGHLIGHT_COLOR,
              "line-width": HIGHLIGHT_STROKE_WIDTH,
              "line-opacity": 1,
            },
          });
        }

        // Add line layer if it doesn't exist
        if (!map.getLayer(HIGHLIGHT_LINE_LAYER_ID)) {
          map.addLayer({
            id: HIGHLIGHT_LINE_LAYER_ID,
            type: "line",
            source: HIGHLIGHT_SOURCE_ID,
            filter: [
              "match",
              ["geometry-type"],
              ["LineString", "MultiLineString"],
              true,
              false,
            ],
            paint: {
              "line-color": HIGHLIGHT_COLOR,
              "line-width": HIGHLIGHT_STROKE_WIDTH + 2,
              "line-opacity": 1,
            },
          });
        }

        // Add point layer if it doesn't exist
        if (!map.getLayer(HIGHLIGHT_POINT_LAYER_ID)) {
          map.addLayer({
            id: HIGHLIGHT_POINT_LAYER_ID,
            type: "circle",
            source: HIGHLIGHT_SOURCE_ID,
            filter: [
              "match",
              ["geometry-type"],
              ["Point", "MultiPoint"],
              true,
              false,
            ],
            paint: {
              "circle-radius": 12,
              "circle-color": HIGHLIGHT_COLOR,
              "circle-opacity": 0.8,
              "circle-stroke-color": "#FFFFFF",
              "circle-stroke-width": 3,
            },
          });
        }

        moveHighlightLayersToTop();
      } catch (error) {
        console.warn("Failed to add highlight layers:", error);
      }
    };

    if (map.loaded()) {
      addLayers();
    } else {
      map.once("load", addLayers);
    }

    // Cleanup function
    return () => {
      try {
        if (map.getLayer(`${HIGHLIGHT_POLYGON_LAYER_ID}-outline`)) {
          map.removeLayer(`${HIGHLIGHT_POLYGON_LAYER_ID}-outline`);
        }
        if (map.getLayer(HIGHLIGHT_POLYGON_LAYER_ID)) {
          map.removeLayer(HIGHLIGHT_POLYGON_LAYER_ID);
        }
        if (map.getLayer(HIGHLIGHT_LINE_LAYER_ID)) {
          map.removeLayer(HIGHLIGHT_LINE_LAYER_ID);
        }
        if (map.getLayer(HIGHLIGHT_POINT_LAYER_ID)) {
          map.removeLayer(HIGHLIGHT_POINT_LAYER_ID);
        }
        if (map.getSource(HIGHLIGHT_SOURCE_ID)) {
          map.removeSource(HIGHLIGHT_SOURCE_ID);
        }
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [mapInstance, moveHighlightLayersToTop]);

  // Update highlight layer when highlighted feature changes
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const source = map.getSource(HIGHLIGHT_SOURCE_ID);
    if (!source || source.type !== "geojson") return;

    if (highlightedFeature) {
      // Create feature collection with the highlighted feature
      const featureCollection: FeatureCollection<Geometry> = {
        type: "FeatureCollection",
        features: [highlightedFeature.feature],
      };

      // Type assertion for GeoJSON source
      (source as GeoJSONSource).setData(featureCollection);
    }

    moveHighlightLayersToTop();

    if (!highlightedFeature) {
      // Clear highlight
      (source as GeoJSONSource).setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  }, [mapInstance, highlightedFeature, moveHighlightLayersToTop]);

  // Clear highlight when user clicks on empty area
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const handleMapClick = (e: MapMouseEvent) => {
      // Check if click is on empty space (no features)
      const features = map.queryRenderedFeatures(e.point);

      // If no features clicked, or only clicked on highlight layers, clear highlight
      const nonHighlightFeatures = features.filter(
        (f) =>
          !f.layer.id.startsWith("highlight-") &&
          !f.layer.id.startsWith("gl-draw")
      );

      if (nonHighlightFeatures.length === 0) {
        clearHighlight();
      }
    };

    map.on("click", handleMapClick);

    const handleStyleData = () => {
      moveHighlightLayersToTop();
    };

    const handleLayerReorder = () => {
      moveHighlightLayersToTop();
    };

    map.on("styledata", handleStyleData);
    window.addEventListener("arcgis-layers-reordered", handleLayerReorder);

    return () => {
      map.off("click", handleMapClick);
      map.off("styledata", handleStyleData);
      window.removeEventListener("arcgis-layers-reordered", handleLayerReorder);
    };
  }, [mapInstance, clearHighlight, moveHighlightLayersToTop]);

  return null; // This component doesn't render anything
};
